import { useQuery } from "@tanstack/react-query";
import type { FacilityPermissionLevel } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { facilityApi } from "../../api/client.js";

/**
 * The caller's permitted facility tree (whole tree, one request — derive-on-read on the server). Mirrors `useMe`:
 * keyed by tenant so switching realms can't serve a cached tree, `enabled` until there is a session. Returns the flat,
 * path-ordered node list; the UI nests + searches it client-side.
 */
export function useFacilities(session: AuthSession | null, permission: FacilityPermissionLevel = "view") {
  return useQuery({
    // Keyed by tenant AND subject — the tree is derived per-caller server-side, so two users in the same realm must
    // NOT share a cache entry (panel finding). Mirrors useMe's ["me", realm, sub].
    queryKey: ["facilities", session?.realm, session?.sub, permission],
    enabled: session !== null,
    queryFn: async () => {
      const res = await facilityApi.listFacilities({ query: { permission } });
      if (res.status !== 200) throw new Error(`GET /facilities returned ${res.status}`);
      return res.body.facilities;
    },
  });
}
